import * as Cesium from 'cesium';
import { showAlert } from 'helper';
import { makeStyles } from "@mui/styles";
import { useDispatch } from "react-redux";
import GeoAIInterface from 'utils/GeoAIInterface';
import React, { useState, useEffect } from "react";
import { GeoAIFeatureTypesEnum } from '../../../Enums';
import exportNotesIcon from "./Icons/export_notes.svg";
import displayIcon from "./Icons/display_external_input.svg";
import TooltipIconButton from "components/TooltipIconButton";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import downloadForOfflineIcon from "./Icons/download_for_offline.svg";
import { Tooltip, Select, MenuItem, IconButton, CircularProgress } from "@mui/material";

const GeoAI = (props) => {
    const [skip, setSkip] = useState(0);
    const [limit, setLimit] = useState(10);
    const [usage, setUsage] = useState(0);
    const [requests, setRequests] = useState([]);
    const [totalRequests, setTotalRequests] = useState(0);
    const dispatch = useDispatch();

    const getRequests = () => {
        GeoAIInterface.instance.getGeoAIRequests(skip, limit).then(response => {
            setRequests(response.data.data);
            setTotalRequests(response.data.count);
        }).catch(error => {
            showAlert('error', 'Error in getting GeoAI requests.');
        });
    }

    const getGeoAIRequestUsage = () => {
        GeoAIInterface.instance.getGeoAIRequestUsage().then(response => {
            setUsage(response.data.monthly_usage/response.data.monthly_limit);
        }).catch(error => {
            setUsage(0);
            // TODO: showAlert('error', 'Error in getting GeoAI request usage.');
        });
    }

    const calculateArea = (bbox) => {
        // TODOL: const area = Cesium.PolygonPipeline.computeArea2D(corners);
        const [minLon, minLat, maxLon, maxLat] = bbox.map(Number);
        const R = 6371;
        const deg2rad = (deg) => deg * Math.PI / 180;
        const latDiff = Math.abs(Math.sin(deg2rad(maxLat)) - Math.sin(deg2rad(minLat)));
        const lonDiff = Math.abs(deg2rad(maxLon) - deg2rad(minLon));
        const area = R * R * latDiff * lonDiff;
        return area;
    }

    const viewArea = (bbox) => {
        if (window.exportsTablePolygon) {
            window.MAIN_CESIUM_VIEWER.entities.remove(window.exportsTablePolygon);
        }

        const [min_lon, min_lat, max_lon, max_lat] = bbox.map(Number);
        const coords = [[min_lon, min_lat], [min_lon, max_lat], [max_lon, max_lat], [max_lon, min_lat], [min_lon, min_lat]];
        const coordinates_degree = coords.reduce((arr, c) => arr.concat(c), []);
        window.exportsTablePolygon = window.MAIN_CESIUM_VIEWER.entities.add({
            polygon: {
                hierarchy: Cesium.Cartesian3.fromDegreesArray(coordinates_degree),
                material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
                outline: true,
                outlineColor: new Cesium.Color.fromCssColorString('#487BF2')
            }
        });

        window.MAIN_CESIUM_VIEWER.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees((min_lon + max_lon) / 2, (min_lat + max_lat) / 2, 1000),
            orientation: {
                heading: 0,
                pitch: Cesium.Math.toRadians(-90),
                roll: 0,
            },
            duration: 1.5
        });
    }

    const viewMetadata = (geoai_req) => {
        dispatch({ type: 'TOGGLE_REQINFO_MODAL', show: true });
        const area = calculateArea(geoai_req.bbox)
        var info = {
            ID: {
                value: geoai_req.id
            },
            'Created at': {
                value: new Date(`${geoai_req.created_at}`).toLocaleString()
            },
            Collection: {
                value: geoai_req.layer_name.split(':')[0]
            },
            Mission: {
                value: geoai_req.layer_name.split(':')[1]
            },
            'Feature Type': {
                value: Object.keys(GeoAIFeatureTypesEnum).find(key => GeoAIFeatureTypesEnum[key] === geoai_req.feature_type)
            },
            Area: {
                value: area > 1 ? `${area.toFixed(2)} km²` : `${(area * 1_000_000).toFixed(2)} m²`
            },
            '# of Detections': {
                value: geoai_req.result.features?.length || 0
            }
        }
        dispatch({
            type: 'SET_REQINFO_METADATA', reqInfoMetadata: info
        });
    }

    const downloadResult = (geojson) => {
        if (!geojson) {
            showAlert('error', 'No result to download.');
            return;
        }

        const dataStr = JSON.stringify(geojson, null, 2);
        const blob = new Blob([dataStr], { type: "application/geo+json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "result.geojson";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const nextPage = () => {
        if (skip + limit < totalRequests)
            setSkip(skip + limit);
    }

    const prevPage = () => {
        if (skip - limit >= 0)
            setSkip(skip - limit);
    }

    const CustomTooltip = (props) => {
        const useStyles = makeStyles((theme) => ({
            tooltip: {
                color: "white",
                fontSize: "12px !important",
                backgroundColor: "#5A5A5A",
                borderRadius: 3,
                fontFamily: 'Roboto',
                padding: '8px 10px'
            }
        }));
        const classes = useStyles();
        return <Tooltip classes={classes} {...props} />;
    }

    useEffect(() => {
        getRequests();
        getGeoAIRequestUsage();

        return (() => {
            if (window.exportsTablePolygon) {
                window.MAIN_CESIUM_VIEWER.entities.remove(window.exportsTablePolygon);
            }
        })
    }, [])

    useEffect(() => {
        getRequests();
    }, [skip, limit])

    return (<>
        <div style={{ fontSize: 21, marginBottom: 15 }}>GeoAI Requests</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: "1.1rem", color: "#fff" }}>Monthly Usage</span>
                <span style={{ fontSize: "1rem", color: "#fff" }}>{usage}% used</span>
            </div>
            <CircularProgress
                variant="determinate"
                value={usage > 1 ? usage : 1}
                size={48}
                thickness={5}
                style={{ color: "#487bf2", background: "#191919", borderRadius: "50%" }}>
            </CircularProgress>
        </div>

        <table style={{ width: '100%' }}>
            <tr style={{ fontSize: 11, fontWeight: "bold", borderTop: 'white solid 1px', borderBottom: 'white solid 1px', height: 45 }}>
                <td>DATE</td>
                <td>FEATURE</td>
                <td>METADATA</td>
                <td>AREA</td>
                <td>DOWNLOAD</td>
            </tr>
            {requests.map(request => {
                return (
                    <tr style={{ borderTop: 'white solid 1px', borderBottom: 'white solid 1px', height: 45 }}>
                        <td>{new Date(`${request.created_at}`).toLocaleDateString({ year: 'numeric', month: 'numeric', day: 'numeric' })}</td>
                        <td>{Object.keys(GeoAIFeatureTypesEnum).find(key => GeoAIFeatureTypesEnum[key] === request.feature_type)}</td>
                        <td><CustomTooltip title="View Metadata" placement="bottom-start"><IconButton onClick={() => viewMetadata(request)}><img src={exportNotesIcon} style={{ height: '24px' }} /></IconButton></CustomTooltip></td>
                        <td><CustomTooltip title="View Area" placement="bottom-start"><IconButton onClick={() => viewArea(request.bbox)}><img src={displayIcon} style={{ height: '24px' }} /></IconButton></CustomTooltip></td>
                        <td><CustomTooltip title="Download Results" placement="bottom-start"><IconButton onClick={() => downloadResult(request.result)}><img src={downloadForOfflineIcon} style={{ height: '24px' }} /></IconButton></CustomTooltip></td>
                    </tr>
                )
            })}
        </table>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 15,
            marginBottom: 5,
            flexWrap: 'wrap'
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: "1.1rem", color: "#fff" }}>Rows per page:</span>
                <Select
                    value={limit}
                    variant="outlined"
                    size="small"
                    style={{
                        marginLeft: 12,
                        marginRight: 8,
                        color: "#fff",
                        background: "#323232",
                        borderRadius: 4,
                        minWidth: 60
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                background: "#232323",
                                color: "#fff"
                            }
                        }
                    }}
                    onChange={(evt) => setLimit(evt.target.value)}>
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                </Select>
                {totalRequests === 0 ? null : (
                    <span style={{ fontSize: "1.1rem", marginLeft: 18, color: "#bbb" }}>
                        {skip + 1} - {skip + limit <= totalRequests ? skip + limit : totalRequests} of {totalRequests}
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <TooltipIconButton title="Previous" ariaLabel="Previous" size={35} onClick={prevPage}>
                    <ChevronLeft />
                </TooltipIconButton>
                <TooltipIconButton title="Next" ariaLabel="Next" size={35} onClick={nextPage}>
                    <ChevronRight />
                </TooltipIconButton>
            </div>
        </div>

    </>)
}

export default GeoAI;